import axios from "axios";
import { getUserTimezone } from "@helpers/timezone";
import {
  subscribeOnStream,
  unsubscribeFromStream,
} from "./streaming";
import {
  getSecondsBars,
  getSecondsIntervalMs,
} from "./secondsHistory";
import store from "../../../../../../redux/store";

// Секундні свічки будуються з угод (secondsHistory), Binance віддає лише ці
const SECONDS_MULTIPLIERS = ["15", "30"];

export const SUPPORTED_RESOLUTIONS = [
  "15S",
  "30S",
  "1",
  "5",
  "10",
  "15",
  "30",
  "45",
  "60",
  "240",
  "D",
  "W",
  "M",
];

const configurationData = {
  supported_resolutions: SUPPORTED_RESOLUTIONS,
};

const alignedBarOpenTimeMs = (resolution, time) => {
  const r = String(resolution);
  const HOUR_MS = 60 * 60 * 1000;

  if (r === "D" || r === "1D") {
    const date = new Date(time);

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    );
  }

  if (r === "W" || r === "1W") {
    const date = new Date(time);
    const day = date.getUTCDay();
    const daysFromMonday = (day + 6) % 7;

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() - daysFromMonday,
    );
  }

  if (r === "M" || r === "1M") {
    const date = new Date(time);

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      1,
    );
  }

  if (/^\d+$/.test(r)) {
    const resolutionMs =
      Number(r) * 60 * 1000;

    return (
      Math.floor(time / resolutionMs) *
      resolutionMs
    );
  }

  return Math.floor(time / HOUR_MS) * HOUR_MS;
};

const getResolutionConfig = (resolution) => {
  const r = String(resolution);

  const nativeIntervals = {
    "1": "1m",
    "3": "3m",
    "5": "5m",
    "15": "15m",
    "30": "30m",
    "60": "1h",
    "120": "2h",
    "240": "4h",
    "360": "6h",
    "480": "8h",
    "720": "12h",
    D: "1d",
    "1D": "1d",
    W: "1w",
    "1W": "1w",
    M: "1M",
    "1M": "1M",
  };

  if (nativeIntervals[r]) {
    return nativeIntervals[r];
  }

  /*
   * Binance не має нативних 10m та 45m.
   *
   * Тому:
   * 10m будуємо з 5m;
   * 45m будуємо з 15m.
   *
   * Та сама логіка працює і для інших
   * числових resolution, кратних базовим.
   */
  if (/^\d+$/.test(r)) {
    const targetMinutes = Number(r);
    const availableBaseMinutes = [
      30,
      15,
      5,
      3,
      1,
    ];

    const baseMinutes =
      availableBaseMinutes.find(
        (minutes) =>
          targetMinutes % minutes === 0,
      ) || 1;

    return `${baseMinutes}m`;
  }

  return "1m";
};

const aggregateBars = (bars, resolution) => {
  const groupedBars = new Map();

  bars.forEach((bar) => {
    const targetTime = alignedBarOpenTimeMs(
      resolution,
      bar.time,
    );

    const existing = groupedBars.get(targetTime);

    if (!existing) {
      groupedBars.set(targetTime, {
        time: targetTime,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume,
      });

      return;
    }

    groupedBars.set(targetTime, {
      ...existing,
      high: Math.max(existing.high, bar.high),
      low: Math.min(existing.low, bar.low),
      close: bar.close,
      volume:
        existing.volume == null &&
        bar.volume == null
          ? undefined
          : (existing.volume || 0) +
            (bar.volume || 0),
    });
  });

  return Array.from(groupedBars.values()).sort(
    (a, b) => a.time - b.time,
  );
};

let widgetIdCounter = 0;

/**
 * Повертає новий об'єкт datafeed з унікальним widgetId.
 * Кожен графік повинен отримати свій екземпляр,
 * щоб підписки в streaming.js не перетиналися.
 *
 * @returns {object} datafeed з полем widgetId
 */
export const createDatafeed = () => {
  widgetIdCounter += 1;
  const widgetId = `w${widgetIdCounter}`;

  return {
    widgetId,

    onReady: (callback) => {
      setTimeout(() => {
        callback(configurationData);
      });
    },

    getBars: async (
      symbolInfo,
      resolution,
      periodParams,
      onHistoryCallback,
    ) => {
      try {
        const secondsIntervalMs =
          getSecondsIntervalMs(resolution);

        if (secondsIntervalMs) {
          const { bars, noData } = await getSecondsBars(
            symbolInfo.ticker.toUpperCase(),
            secondsIntervalMs,
            periodParams,
          );

          onHistoryCallback(bars, { noData });
          return;
        }

        const { from, to } = periodParams;

        const interval =
          getResolutionConfig(resolution);

        const params = {
          symbol: symbolInfo.ticker.toUpperCase(),
          interval,
          startTime: Math.floor(from * 1000),
          endTime: Math.floor(to * 1000),
          limit: 1500,
        };

        /*
         * OHLC — з mark price: за цією ціною бек відкриває і закриває позиції,
         * тому лінії входу й ліквідації лежать на свічках. Об'єм у mark-свічок
         * нульовий, його беремо зі звичайних kline за той самий час.
         */
        const [markResponse, tradesResponse] = await Promise.all([
          axios.get(
            "https://fapi.binance.com/fapi/v1/markPriceKlines",
            { params },
          ),
          axios
            .get("https://fapi.binance.com/fapi/v1/klines", { params })
            .catch(() => ({ data: [] })),
        ]);

        if (
          !Array.isArray(markResponse.data) ||
          markResponse.data.length === 0
        ) {
          onHistoryCallback([], {
            noData: true,
          });

          return;
        }

        const volumeByTime = new Map(
          (Array.isArray(tradesResponse.data)
            ? tradesResponse.data
            : []
          ).map((row) => [Number(row[0]), Number(row[5])]),
        );

        const baseBars = markResponse.data
          .map((item) => ({
            time: Number(item[0]),
            open: Number(item[1]),
            high: Number(item[2]),
            low: Number(item[3]),
            close: Number(item[4]),
            volume: volumeByTime.get(Number(item[0])),
          }))
          .filter(
            (bar) =>
              Number.isFinite(bar.time) &&
              Number.isFinite(bar.open) &&
              Number.isFinite(bar.high) &&
              Number.isFinite(bar.low) &&
              Number.isFinite(bar.close),
          )
          .sort((a, b) => a.time - b.time);

        const aggregatedBars = aggregateBars(
          baseBars,
          resolution,
        );

        /*
         * Графік не повинен отримувати
         * два бари з однаковим timestamp.
         */
        const uniqueBars = Array.from(
          new Map(
            aggregatedBars.map((bar) => [
              bar.time,
              bar,
            ]),
          ).values(),
        ).sort((a, b) => a.time - b.time);

        onHistoryCallback(uniqueBars, {
          noData: uniqueBars.length === 0,
        });
      } catch (error) {
        console.error(
          "Datafeed error:",
          error,
        );

        onHistoryCallback([], {
          noData: true,
        });
      }
    },

    resolveSymbol: async (
      symbolName,
      onSymbolResolvedCallback,
    ) => {
      const state = store.getState();

      const rawPriceScale = Number(
        state.futures.pair.pricescale,
      );

      /*
       * Number(undefined) повертає NaN.
       * Оператор ?? не замінює NaN на fallback.
       */
      const pricescale =
        Number.isFinite(rawPriceScale) &&
        rawPriceScale > 0
          ? rawPriceScale
          : 1;

      const symbol = {
        ticker: symbolName,
        name: symbolName,
        full_name: symbolName,
        session: "24x7",
        timezone: getUserTimezone(),
        minmov: 1,
        pricescale,
        has_intraday: true,
        has_seconds: true,
        seconds_multipliers: SECONDS_MULTIPLIERS,
        has_daily: true,
        has_weekly_and_monthly: true,
        supported_resolutions:
          configurationData.supported_resolutions,
        volume_precision: 2,
        data_status: "streaming",
      };

      setTimeout(() => {
        onSymbolResolvedCallback(symbol);
      });
    },

    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
    ) => {
      subscribeOnStream(
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscriberUID,
        widgetId,
      );
    },

    unsubscribeBars: (subscriberUID) => {
      unsubscribeFromStream(subscriberUID, widgetId);
    },
  };
};
